import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Modal } from 'flowbite';
import moment from 'moment';
import { AnimationOptions } from 'ngx-lottie';
import { IBookingDetails } from 'src/app/models/booking/bookingDetails';
import { InputType, UserRole } from 'src/app/models/enums';
import {
  BookingsService,
  StorageService,
  UtilsService,
} from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';

@Component({
  selector: 'app-modal-confirmation-text',
  templateUrl: './modal-confirmation-text.component.html',
  styleUrl: './modal-confirmation-text.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ModalConfirmationTextComponent implements OnInit, OnChanges {
  @Input() id: string;
  @Input() modal: Modal;
  @Input() title: string;
  @Input() text: string;
  @Input() moreInfo?: string;
  @Input() reason: string;
  @Input() deleting?: boolean;
  @Input() deletingText: string;
  @Input() bookings?: IBookingDetails[];
  @Input() hasBookings?: boolean;
  @Input() isRoundTrip?: boolean;
  @Input() hasTicketing?: boolean;
  @Input() reasonSuggestions?: string[];
  @Input() closeLabel?: string;
  @Output() action = new EventEmitter<any>();

  public InputType = InputType;
  public UserRole = UserRole;
  public images = images;

  public formControl: FormGroup;
  public invalid: any[] = [];

  public hasError: boolean = false;
  public error: string = '';
  public lengthError = true;

  public chars = 0;
  public maxLength = 150;

  public showRefundToggle: boolean = false;
  public showRelatedToggle: boolean = false;
  public notify: boolean = true;
  public refund: boolean = true;
  public fullRefund: boolean = false;
  public related: boolean = true;
  public relatedBookingsInfo: string[] = [];

  public userRole: UserRole;

  public optionsBus: AnimationOptions = {
    path: '/assets/animations/bus.json',
  };

  constructor(
    public literalService: LiteralService,
    public utilsService: UtilsService,
    public bookingsService: BookingsService,
    public translate: TranslateService,
    public storageService: StorageService,
  ) {}

  ngOnInit(): void {
    this.getRole();
    this.showRefundToggle = false;
    this.showRelatedToggle = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['bookings'] && changes['bookings'].currentValue) {
      this.showRefundToggle = changes['bookings'].currentValue.some(
        (booking: IBookingDetails) =>
          booking?.paidDateTime &&
          booking?.price > 0 &&
          !booking?.validatedDateTime,
      );
      this.showRelatedToggle = changes['bookings'].currentValue.some(
        (booking: IBookingDetails) =>
          booking?.relatedBookings?.filter(
            (relatedId) => relatedId !== booking!.id,
          ).length! > 0,
      );
      if (this.showRelatedToggle) {
        this.relatedBookingsInfo = [];
        changes['bookings'].currentValue.map((booking: IBookingDetails) => {
          booking?.relatedBookingsInfo?.map(
            (bookingDetail: IBookingDetails) => {
              this.relatedBookingsInfo.push(
                `${bookingDetail.origin.name} - ${bookingDetail.destination.name}, ${moment(bookingDetail.availability.dateTime).format('HH:mm - ddd - DD/MM/YYYY')}`,
              );
            },
          );
        });
      }
    }
  }

  async getRole() {
    this.userRole = await this.storageService.getRole();
  }

  change(reason: any) {
    this.reason = reason;
  }

  isReasonSelected(suggestion: string): boolean {
    return this.reason === this.literalService.get(suggestion, true);
  }

  selectReasonSuggestion(suggestion: string) {
    const translated = this.literalService.get(suggestion, true);
    this.reason = this.isReasonSelected(suggestion) ? '' : translated;
    this.onChange();
  }

  close() {
    this.modal.toggle();
    this.reason = '';
    this.refund = true;
    this.fullRefund = false;
    this.related = true;
  }

  onChange() {
    this.chars = this.reason.length;
    this.lengthError = this.chars > this.maxLength;
  }

  onSubmit() {
    const errors = this.findInvalidControls();
    if (errors && 'required' in errors) {
      this.hasError = true;
      this.error = this.literalService.get('errors.requiredField', true);
    } else if (errors && 'maxlength' in errors) {
      this.hasError = true;
      this.error = this.literalService.get('errors.maxlength', true, {
        length: this.maxLength.toString(),
      });
    } else {
      this.hasError = false;
      this.error = '';
      let result: any = {
        reason: this.reason,
        sendNotification: this.notify,
      };
      if (this.showRefundToggle) {
        result = {
          ...result,
          refund: this.refund || this.fullRefund,
          fullRefund: this.fullRefund,
        };
      }
      if (this.showRelatedToggle) {
        result = {
          ...result,
          related: this.related,
        };
      }
      this.action.emit(result);
    }
  }

  findInvalidControls() {
    this.formControl = new FormGroup({
      reason: new FormControl(this.reason, [
        // Validators.required,
        Validators.maxLength(this.maxLength),
      ]),
    });
    return this.formControl.controls['reason'].errors;
  }

  isConfirmDisabled() {
    if (
      (this.showRefundToggle || this.hasTicketing) &&
      (this.showRelatedToggle || this.isRoundTrip)
    )
      return (this.refund || this.fullRefund) && !this.related;
    else return false;
  }
}
